import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOnboarding, ModuleTips } from '../components/onboarding';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Search,
  Home,
  Car,
  Monitor,
  Briefcase,
  Megaphone,
  Plane,
  GraduationCap,
  Shield,
  PiggyBank,
  Package,
  UtensilsCrossed,
  MoreHorizontal,
  DollarSign,
  TrendingUp,
  Upload,
  FolderUp,
  FileSpreadsheet,
  Sparkles,
  X,
  ChevronDown,
  Edit2
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Home, Car, Monitor, Briefcase, Megaphone, Plane,
  GraduationCap, Shield, PiggyBank, Package, UtensilsCrossed, MoreHorizontal
};

const Deductions = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const { triggerAction } = useOnboarding();
  const [deductions, setDeductions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteDeductionId, setConfirmDeleteDeductionId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLimits, setUploadLimits] = useState({ maxBulkUploadFiles: 20, maxCsvSizeMB: 10 });
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
    fetchUploadLimits();
    // Trigger onboarding action when user views deductions page
    triggerAction('view_deductions');
  }, []);

  const fetchUploadLimits = async () => {
    try {
      const response = await axios.get(`${API}/upload-limits`);
      setUploadLimits(response.data);
    } catch (error) {
      // Silently fail - upload limits are not critical
    }
  };

  const fetchData = async () => {
    try {
      const [deductionsRes, categoriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/deductions`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/deduction-categories`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/deductions/summary`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      setDeductions(deductionsRes.data);
      // API returns {categories: [...]} so extract the array
      setCategories(categoriesRes.data.categories || categoriesRes.data || []);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error(t('deductions.failed_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category) {
      toast.error(t('deductions.select_category_error'));
      return;
    }
    try {
      await axios.post(`${API}/deductions`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, { headers: getAuthHeaders(), withCredentials: true });
      
      toast.success(t('deductions.added'));
      setDialogOpen(false);
      setFormData({ category: '', name: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
      fetchData();
      // Trigger onboarding action when user adds a deduction
      triggerAction('add_deduction');
    } catch (error) {
      toast.error(t('deductions.failed_add'));
    }
  };

  const handleDelete = async (deductionId) => {
    if (confirmDeleteDeductionId !== deductionId) {
      setConfirmDeleteDeductionId(deductionId);
      toast.info('Click delete again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDeleteDeductionId(null), 3000);
      return;
    }
    setConfirmDeleteDeductionId(null);
    
    try {
      await axios.delete(`${API}/deductions/${deductionId}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(t('deductions.deleted'));
      fetchData();
    } catch (error) {
      toast.error(t('deductions.failed_delete'));
    }
  };

  const handleCategoryChange = async (deductionId, newCategory) => {
    try {
      const response = await axios.patch(
        `${API}/deductions/${deductionId}/category?category=${newCategory}`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`${t('deductions.category_updated')} ${newCategory.replace(/_/g, ' ')}`);
      fetchData();
    } catch (error) {
      toast.error(t('deductions.failed_update_category'));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = uploadLimits.maxBulkUploadFiles;
    const maxFileSizeMB = uploadLimits.maxCsvSizeMB;
    
    if (files.length > maxFiles) {
      toast.error(t('toast.too_many_files', { max: maxFiles }));
      return;
    }
    
    const validFiles = [];
    for (const file of files) {
      if (file.size > maxFileSizeMB * 1024 * 1024) {
        toast.error(t('toast.file_too_large', { name: file.name, max: maxFileSizeMB }));
      } else {
        validFiles.push(file);
      }
    }
    
    setUploadingFiles(validFiles);
  };

  const handleBulkUpload = async () => {
    if (uploadingFiles.length === 0) {
      toast.error(t('deductions.select_files'));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    uploadingFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post(`${API}/ai/bulk-upload-deductions`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const { total_deductions, total_amount, estimated_tax_savings, categories_found, errors } = response.data;
      
      toast.success(t('deductions.found_deductions', { count: total_deductions, amount: total_amount.toFixed(2) }));
      
      if (estimated_tax_savings > 0) {
        toast.info(t('deductions.estimated_tax_savings', { amount: estimated_tax_savings.toFixed(2) }));
      }
      
      if (categories_found?.length > 0) {
        toast.info(`${t('deductions.categories_found')} ${categories_found.join(', ')}`);
      }
      
      if (errors?.length > 0) {
        errors.forEach(err => {
          toast.error(t('toast.file_error', { file: err.file, error: err.error }));
        });
      }

      setBulkUploadDialogOpen(false);
      setUploadingFiles([]);
      fetchData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || t('deductions.upload_failed'));
      } else {
        toast.error(t('deductions.processing_failed') + ': ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredDeductions = deductions.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         d.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || d.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || { name: categoryId, icon: 'MoreHorizontal' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="deductions-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">{t('deductions.title')}</h1>
            <p className="text-muted-foreground">{t('deductions.subtitle')}</p>
          </div>
          
          <div className="flex gap-2">
            {/* Bulk Upload Button */}
            <Dialog open={bulkUploadDialogOpen} onOpenChange={setBulkUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-upload-deductions-btn">
                  <FolderUp className="w-4 h-4 mr-2" />
                  {t('deductions.bulk_upload')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    {t('deductions.ai_bulk_upload')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('deductions.bulk_upload_desc')}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>{t('deductions.ai_will')}</strong>
                    </p>
                    <ul className="text-xs text-blue-700 mt-1 space-y-1">
                      <li>• {t('deductions.ai_extract_amounts')}</li>
                      <li>• {t('deductions.ai_categorize')}</li>
                      <li>• {t('deductions.ai_calculate')}</li>
                      <li>• {t('deductions.ai_estimate')}</li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2 font-medium">
                      {t('deductions.limits_info', { maxFiles: uploadLimits.maxBulkUploadFiles, maxSize: uploadLimits.maxCsvSizeMB })}
                    </p>
                  </div>
                  
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                      multiple
                      accept=".csv,.txt,.pdf"
                      data-testid="deduction-file-input"
                    />
                    <FolderUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {t('deductions.select_files')}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('deductions.csv_txt_pdf')}
                    </p>
                  </div>
                  
                  {uploadingFiles.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t('deductions.selected_files')} ({uploadingFiles.length})</Label>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {uploadingFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm bg-muted p-2 rounded">
                            <span className="flex items-center gap-2 truncate">
                              <FileSpreadsheet className="w-4 h-4 flex-shrink-0" />
                              {file.name}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => setUploadingFiles(files => files.filter((_, i) => i !== idx))}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {isUploading && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{t('deductions.processing_ai')}</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} />
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleBulkUpload} 
                    disabled={uploadingFiles.length === 0 || isUploading}
                    className="w-full"
                    data-testid="process-deduction-upload-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('deductions.process_files', { count: uploadingFiles.length })}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Add Deduction Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-deduction-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('deductions.add_deduction')}
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('deductions.add_new')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('deductions.category')}</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger data-testid="deduction-category-select">
                      <SelectValue placeholder={t('deductions.select_category')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('deductions.name')}</Label>
                  <Input
                    placeholder={t('deductions.name_placeholder')}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="deduction-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('deductions.amount')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    required
                    data-testid="deduction-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('deductions.date')}</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    data-testid="deduction-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('deductions.description')}</Label>
                  <Textarea
                    placeholder={t('deductions.add_notes')}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    data-testid="deduction-description-input"
                  />
                </div>

                <Button type="submit" className="w-full" data-testid="submit-deduction-btn">
                  {t('deductions.add_deduction')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="shadow-soft border-border/40" data-testid="total-deductions-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('deductions.total_deductions')}</p>
                <p className="text-2xl font-bold mono">{formatCurrency(summary?.total_deductions)}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40" data-testid="estimated-savings-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('deductions.estimated_savings')}</p>
                <p className="text-2xl font-bold text-emerald-600 mono">
                  {/* Commandment II: estimated_tax_savings from backend DTO */}
                  {formatCurrency(summary?.estimated_tax_savings ?? 0)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40" data-testid="items-count-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('deductions.items_tracked')}</p>
                <p className="text-2xl font-bold mono">{summary?.count || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('deductions.search_deductions')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-deductions"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-category">
              <SelectValue placeholder={t('deductions.all_categories')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('deductions.all_categories')}</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Deductions List */}
        <Card className="shadow-soft border-border/40" data-testid="deductions-list">
          <CardContent className="p-0">
            {filteredDeductions.length > 0 ? (
              <div className="divide-y divide-border">
                {filteredDeductions.map((deduction) => {
                  const catInfo = getCategoryInfo(deduction.category);
                  const IconComponent = iconMap[catInfo.icon] || MoreHorizontal;
                  
                  return (
                    <div 
                      key={deduction.deduction_id} 
                      className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors"
                      data-testid={`deduction-item-${deduction.deduction_id}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5 text-muted-foreground" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium truncate">{deduction.name}</span>
                          
                          {/* Editable Category */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 hover:bg-emerald-500 hover:text-white transition-colors text-xs group">
                                <span className="group-hover:text-white">{catInfo.name}</span>
                                <ChevronDown className="w-3 h-3 group-hover:text-white" />
                                {deduction.user_override && (
                                  <Edit2 className="w-3 h-3 text-blue-500 group-hover:text-white" />
                                )}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-2 bg-white border-slate-200" align="start">
                              <p className="text-xs font-medium text-slate-500 mb-2 px-2">{t('deductions.change_category')}</p>
                              <div className="max-h-64 overflow-y-auto space-y-1">
                                {categories.map(cat => (
                                  <button
                                    key={cat.id}
                                    onClick={() => handleCategoryChange(deduction.deduction_id, cat.id)}
                                    className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                                      deduction.category === cat.id 
                                        ? 'bg-emerald-500 text-white font-medium' 
                                        : 'text-slate-700 hover:bg-emerald-500 hover:text-white'
                                    }`}
                                  >
                                    {cat.name}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                          
                          {deduction.ai_confidence && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition-colors">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI {deduction.ai_confidence}%
                            </Badge>
                          )}
                          {deduction.status === 'draft' && (
                            <Badge variant="secondary" className="text-xs bg-orange-200 text-slate-900 font-medium">Draft</Badge>
                          )}
                        </div>
                        {deduction.description && (
                          <p className="text-sm text-slate-500 truncate">{deduction.description}</p>
                        )}
                        <p className="text-xs text-slate-400">{deduction.date}</p>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-semibold mono text-slate-900">{formatCurrency(deduction.amount)}</p>
                        {deduction.deductible_percentage && deduction.deductible_percentage < 100 && (
                          <p className="text-xs text-muted-foreground">{deduction.deductible_percentage}% {t('deductions.deductible')}</p>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(deduction.deduction_id)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`delete-deduction-${deduction.deduction_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">{t('deductions.no_deductions')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('deductions.start_tracking')}
                </p>
                <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-deduction">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('deductions.add_first')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        {summary?.by_category && Object.keys(summary.by_category).length > 0 && (
          <Card className="shadow-soft border-border/40" data-testid="category-breakdown">
            <CardHeader>
              <CardTitle>{t('deductions.by_category')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Commandment II: use by_category_with_pct from backend DTO when available */}
              {(summary.by_category_with_pct ?? Object.entries(summary.by_category ?? {}).map(([category, amount]) => ({
                category, amount, percentage: summary.total_deductions > 0 ? (amount / summary.total_deductions * 100) : 0
              }))).map(({ category: catId, amount, percentage }) => {
                  const catInfo = getCategoryInfo(catId);
                  const IconComponent = iconMap[catInfo.icon] || MoreHorizontal;
                  
                  return (
                    <div key={catId} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                        <IconComponent className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">{catInfo.name}</span>
                          <span className="text-sm mono">{formatCurrency(amount)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="deductions" />
  </>
  );
};

export default Deductions;
